// Grievance Form (section type 3000, variant 3000) — admin editor field config.
// Every label, color, option list and the email/backend settings are configurable.
// `resend_api_key` is write-only: it is never returned to the editor, and saving it
// blank keeps the currently stored key (see server services/grievanceForm.js).
//
// NOTE: each field's `value` is set to its default so the admin form is pre-filled
// on widget creation. buildDefaultFieldValues() uses `value ?? defaultValue`, so an
// empty `value` would render blank fields — keep `value` === `defaultValue`.

export const GRIEVANCE_FORM_3000_CONSTANTS = {
  DEFAULT_ROW: {
    id: "1",
    status: true,

    // email / backend
    recipient_email: "",
    resend_api_key: "",
    from_email: "Grievance <onboarding@resend.dev>",
    email_subject: "Grievance: {subject} - {queryType}",

    // heading
    heading: "Submit Your Grievance",
    heading_color: "#47456E",

    // labels & placeholders
    name_label: "Name",
    name_placeholder: "Enter your full name",
    email_label: "Email Id",
    email_placeholder: "Enter your email",
    mobile_label: "Mobile No",
    mobile_placeholder: "Enter your mobile number",
    query_type_label: "Query Type",
    query_type_placeholder: "Select an Option",
    query_type_options:
      "Academic,Administrative,Examination,Fee Related,Technical Issue,Others",
    category_label: "Category",
    category_placeholder: "Select an Option",
    category_options: "Complaint,Query,Feedback,Suggestion",
    subject_label: "Subject",
    subject_placeholder: "Enter subject",
    description_label: "Description",
    description_placeholder: "Describe your grievance in detail",

    // attachment
    attachment_enabled: true,
    attachment_checkbox_label: "Do you want to attach related files/screenshots?",
    attachment_label: "Attachment",
    attachment_help_text: "File size should be less than 5 MB (pdf, png, jpg, jpeg)",

    // buttons & messages
    submit_text: "SUBMIT",
    submitting_text: "SUBMITTING...",
    success_message: "Your grievance has been registered successfully.",
    error_message: "Failed to submit grievance. Please try again.",
    required_field_error: "Complete this field.",

    // colors & background
    section_background_color: "#FFFFFF",
    card_background_color: "#F8F8F8",
    label_color: "#333333",
    input_border_color: "#DDDDDD",
    input_background_color: "#FFFFFF",
    input_text_color: "#333333",
    submit_button_color: "#CD5C5C",
    submit_button_text_color: "#FFFFFF",
  },

  GRIEVANCE_FORM_FIELDS_CONFIG: [
    {
      fieldName: "status",
      label: "Show this section",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "",
      required: false,
      defaultValue: true,
    },

    // ---------- Email / backend ----------
    {
      fieldName: "recipient_email",
      label: "Recipient Email(s)",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "grievance@example.com (comma-separated for multiple)",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "resend_api_key",
      label: "Resend API Key",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Leave blank to keep the current key",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "from_email",
      label: "From Email",
      value: "Grievance <onboarding@resend.dev>",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Grievance <onboarding@resend.dev>",
      required: false,
      defaultValue: "Grievance <onboarding@resend.dev>",
    },
    {
      fieldName: "email_subject",
      label: "Email Subject — placeholders {subject} and {queryType} are replaced with the submitted values",
      value: "Grievance: {subject} - {queryType}",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Grievance: {subject} - {queryType}",
      required: false,
      defaultValue: "Grievance: {subject} - {queryType}",
    },

    // ---------- Heading ----------
    {
      fieldName: "heading",
      label: "Form Heading",
      value: "Submit Your Grievance",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter form heading",
      required: false,
      defaultValue: "Submit Your Grievance",
    },
    {
      fieldName: "heading_color",
      label: "Heading Color",
      value: "#47456E",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Select heading color",
      required: false,
      defaultValue: "#47456E",
    },

    // ---------- Field labels ----------
    {
      fieldName: "name_label",
      label: "Name — Label",
      value: "Name",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Name",
      required: false,
      defaultValue: "Name",
    },
    {
      fieldName: "name_placeholder",
      label: "Name — Placeholder",
      value: "Enter your full name",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter your full name",
      required: false,
      defaultValue: "Enter your full name",
    },
    {
      fieldName: "email_label",
      label: "Email — Label",
      value: "Email Id",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Email Id",
      required: false,
      defaultValue: "Email Id",
    },
    {
      fieldName: "email_placeholder",
      label: "Email — Placeholder",
      value: "Enter your email",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter your email",
      required: false,
      defaultValue: "Enter your email",
    },
    {
      fieldName: "mobile_label",
      label: "Mobile — Label",
      value: "Mobile No",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Mobile No",
      required: false,
      defaultValue: "Mobile No",
    },
    {
      fieldName: "mobile_placeholder",
      label: "Mobile — Placeholder",
      value: "Enter your mobile number",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter your mobile number",
      required: false,
      defaultValue: "Enter your mobile number",
    },
    {
      fieldName: "query_type_label",
      label: "Query Type — Label",
      value: "Query Type",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Query Type",
      required: false,
      defaultValue: "Query Type",
    },
    {
      fieldName: "query_type_placeholder",
      label: "Query Type — Placeholder",
      value: "Select an Option",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Select an Option",
      required: false,
      defaultValue: "Select an Option",
    },
    {
      fieldName: "query_type_options",
      label: "Query Type — Options (comma-separated)",
      value:
        "Academic,Administrative,Examination,Fee Related,Technical Issue,Others",
      valueType: "string",
      filedType: "TEXT_AREA",
      placeholder: "Academic,Administrative,Examination,Fee Related,Technical Issue,Others",
      required: false,
      defaultValue:
        "Academic,Administrative,Examination,Fee Related,Technical Issue,Others",
    },
    {
      fieldName: "category_label",
      label: "Category — Label",
      value: "Category",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Category",
      required: false,
      defaultValue: "Category",
    },
    {
      fieldName: "category_placeholder",
      label: "Category — Placeholder",
      value: "Select an Option",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Select an Option",
      required: false,
      defaultValue: "Select an Option",
    },
    {
      fieldName: "category_options",
      label: "Category — Options (comma-separated)",
      value: "Complaint,Query,Feedback,Suggestion",
      valueType: "string",
      filedType: "TEXT_AREA",
      placeholder: "Complaint,Query,Feedback,Suggestion",
      required: false,
      defaultValue: "Complaint,Query,Feedback,Suggestion",
    },
    {
      fieldName: "subject_label",
      label: "Subject — Label",
      value: "Subject",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Subject",
      required: false,
      defaultValue: "Subject",
    },
    {
      fieldName: "subject_placeholder",
      label: "Subject — Placeholder",
      value: "Enter subject",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Enter subject",
      required: false,
      defaultValue: "Enter subject",
    },
    {
      fieldName: "description_label",
      label: "Description — Label",
      value: "Description",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Description",
      required: false,
      defaultValue: "Description",
    },
    {
      fieldName: "description_placeholder",
      label: "Description — Placeholder",
      value: "Describe your grievance in detail",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Describe your grievance in detail",
      required: false,
      defaultValue: "Describe your grievance in detail",
    },

    // ---------- Attachment ----------
    {
      fieldName: "attachment_enabled",
      label: "Enable File Attachment",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "attachment_checkbox_label",
      label: "Attachment — Checkbox Label",
      value: "Do you want to attach related files/screenshots?",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Do you want to attach related files/screenshots?",
      required: false,
      defaultValue: "Do you want to attach related files/screenshots?",
    },
    {
      fieldName: "attachment_label",
      label: "Attachment — Button Label",
      value: "Attachment",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Attachment",
      required: false,
      defaultValue: "Attachment",
    },
    {
      fieldName: "attachment_help_text",
      label: "Attachment — Help Text",
      value: "File size should be less than 5 MB (pdf, png, jpg, jpeg)",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "File size should be less than 5 MB (pdf, png, jpg, jpeg)",
      required: false,
      defaultValue: "File size should be less than 5 MB (pdf, png, jpg, jpeg)",
    },

    // ---------- Buttons & messages ----------
    {
      fieldName: "submit_text",
      label: "Submit Button Text",
      value: "SUBMIT",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "SUBMIT",
      required: false,
      defaultValue: "SUBMIT",
    },
    {
      fieldName: "submitting_text",
      label: "Submitting Button Text",
      value: "SUBMITTING...",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "SUBMITTING...",
      required: false,
      defaultValue: "SUBMITTING...",
    },
    {
      fieldName: "success_message",
      label: "Success Message",
      value: "Your grievance has been registered successfully.",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Your grievance has been registered successfully.",
      required: false,
      defaultValue: "Your grievance has been registered successfully.",
    },
    {
      fieldName: "error_message",
      label: "Error Message",
      value: "Failed to submit grievance. Please try again.",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Failed to submit grievance. Please try again.",
      required: false,
      defaultValue: "Failed to submit grievance. Please try again.",
    },
    {
      fieldName: "required_field_error",
      label: "Required-field Error Text",
      value: "Complete this field.",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Complete this field.",
      required: false,
      defaultValue: "Complete this field.",
    },

    // ---------- Colors & background ----------
    {
      fieldName: "section_background_color",
      label: "Section Background Color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Select section background color",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "card_background_color",
      label: "Form Card Background Color",
      value: "#F8F8F8",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Select card background color",
      required: false,
      defaultValue: "#F8F8F8",
    },
    {
      fieldName: "label_color",
      label: "Label Color",
      value: "#333333",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Select label color",
      required: false,
      defaultValue: "#333333",
    },
    {
      fieldName: "input_border_color",
      label: "Input Border Color",
      value: "#DDDDDD",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Select input border color",
      required: false,
      defaultValue: "#DDDDDD",
    },
    {
      fieldName: "input_background_color",
      label: "Input Background Color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Select input background color",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "input_text_color",
      label: "Input Text Color",
      value: "#333333",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Select input text color",
      required: false,
      defaultValue: "#333333",
    },
    {
      fieldName: "submit_button_color",
      label: "Submit Button Color",
      value: "#CD5C5C",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Select submit button color",
      required: false,
      defaultValue: "#CD5C5C",
    },
    {
      fieldName: "submit_button_text_color",
      label: "Submit Button Text Color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Select submit button text color",
      required: false,
      defaultValue: "#FFFFFF",
    },
  ],

  METADATA_FIELDS: [],
};
